import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy,
  Inject,
  ViewContainerRef,
  ViewChild, TemplateRef
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Reducers } from '@app/reducers';
import { ConfigService } from '@config/config.service';
import * as selectFromLogin from '@login/ngrx/login.selectors';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ManagerTaskScheduleResponse } from '@services/associations/model/managerTaskScheduleResponse';
import { TaskGridResponse } from '@services/associations/model/taskGridResponse';
import { TaskScheduleResponse } from '@services/associations/model/taskScheduleResponse';
import { PermissionsModel } from '@shared/models/permission.model';
import { CrmService } from '@shared/services/crm.service';
import { SharedService } from '@shared/shared.service';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, catchError, EMPTY, fromEvent, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import dayjs from "dayjs";
import {WebOwnProfileModel} from "@services/back-office/model/webOwnProfileModel";
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

@Component({
    selector: 'app-crm-manager-schedule-dialog',
    templateUrl: './crm-manager-schedule-dialog.component.html',
    styleUrls: ['./crm-manager-schedule-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CrmManagerScheduleDialogComponent implements OnInit, OnDestroy {
  /** Subject to destroy Subscriptions */
  private destroyed$ = new Subject<void>();
  /** Loading */
  loading$ = new BehaviorSubject(false);
  /** Controls */
  dateControl = new UntypedFormControl(this.data.date ? this.data.date : new Date());
  /** Scheduler */
  scheduler$ = new BehaviorSubject<TaskGridResponse[]>([]);
  /** Min Date / Time */
  minDate = new Date();
  /** Disabled Previous Arrow Button */
  disablesPrevious: boolean;
  /** Current User */
  currentUser$: Observable<WebOwnProfileModel | null>;
  currentUser: WebOwnProfileModel | null;
  /** Permissions */
  permissions$: Observable<PermissionsModel[] | null>;
  permissions: PermissionsModel[] | null;
  /** Colors */
  colors: [{id: string, color: string}] = [{id: '', color: '#eefbf0'}];
  /** Selected date */
  selectedStart: string | undefined = undefined;
  selectedEnd: string | undefined = undefined;

  @ViewChild('taskTimeInfo') taskTimeInfo: TemplateRef<any>;
  overlayRef: OverlayRef | null;
  sub: Subscription;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      managerId: string;
      date: Date;
    },
    public dialogRef: MatDialogRef<CrmManagerScheduleDialogComponent>,
    private crmService: CrmService,
    private translateService: TranslateService,
    private toastrService: ToastrService,
    private store$: Store<Reducers>,
    private sharedService: SharedService,
    private config: ConfigService,
    public overlay: Overlay,
    public viewContainerRef: ViewContainerRef
  ) {
    /** Select Current User */
    this.currentUser$ = this.store$.pipe(select(selectFromLogin.selectCurrentUser));
    /** Permissions */
    this.permissions$ = this.store$.pipe(select(selectFromLogin.selectPermissions));
  }

  ngOnInit(): void {
    this.getManagerSchedule();
    this.checkIfPreviousIsDisabled();
    /** Get Permissions From Observable */
    this.getPermissionsFromObservable();
    /** Get Current User From Observable */
    this.getCurrentUserFromObservable();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.loading$.complete();
  }

  /** Get Permissions from Observable */
  getPermissionsFromObservable() {
    this.permissions$.pipe(
      filter(data => !!data),
      take(1)
    ).subscribe((permissions: PermissionsModel[] | null) => {
        this.permissions = permissions;
      }
    );
  }

  /** Get Current User From Observable */
  getCurrentUserFromObservable() {
    this.currentUser$.pipe(
      filter(data => !!data),
      take(1),
    ).subscribe((currentUser: WebOwnProfileModel | null) => {
      this.currentUser = currentUser;
    });
  }

  navCalendar(direction: string) {
    if (direction === 'prev') {
      this.dateControl.setValue(dayjs(this.dateControl.value).subtract(1, 'day').toDate());
      this.getManagerSchedule();
    }

    if (direction === 'next') {
      this.dateControl.setValue(dayjs(this.dateControl.value).add(1, 'day').toDate());
      this.getManagerSchedule();
    }
  }

  /** Check If Previous Button is disabled */
  checkIfPreviousIsDisabled() {
    this.disablesPrevious = dayjs().isSame(dayjs(this.dateControl.value), 'date');
  }

  getManagerSchedule() {
    this.loading$.next(true);
    this.crmService.loadManagerSchedule(this.data.managerId, dayjs(this.dateControl.value).format('YYYY-MM-DD')).pipe(
      takeUntil(this.destroyed$),
      map((response: ManagerTaskScheduleResponse) => {
        if (response.taskGrid) {
          this.scheduler$.next(response.taskGrid);
          this.setColours(response.taskGrid);
        } else {
          this.scheduler$.next([]);
        }
        this.loading$.next(false);
      }),
      catchError((error: HttpErrorResponse) => {
        SharedService.showErrorWithTitle(
          this.translateService,
          this.toastrService,
          `responseErrors.${error.message}`
        );
        this.loading$.next(false);
        return EMPTY;
      })
    ).subscribe();

    this.checkIfPreviousIsDisabled();
  }

  gridClick(hour: number, task: TaskScheduleResponse) {
    if (task.busy && task.taskId) {
      this.sharedService.openCrmItem('TASK', task.taskId!);
    } else {
      const taskMinute = task.from?.substr(3, 2);
      this.selectedStart = `${hour >= 10 ? hour : '0' + hour}:${taskMinute}`;
      this.setTaskDateTime();
    }
  }

  gridDoubleClick(hour: number, task: TaskScheduleResponse, e: any) {
    e.preventDefault();
    if (task.busy && task.taskId) {
      this.sharedService.openCrmItem('TASK', task.taskId!);
    } else {
      const taskHour = hour;
      const taskMinute = task.from?.substr(3, 2);

      if (this.selectedStart === undefined || this.selectedStart && this.selectedEnd) {
        this.selectedStart = `${taskHour}:${taskMinute}`;
        this.selectedEnd = undefined;
        this.openTaskTimeInfo(e);
        return;
      }

      if (this.selectedStart && this.selectedEnd === undefined) {
        this.selectedEnd = `${taskHour}:${taskMinute}`;
        if (Number(this.selectedStart.replace(':', '')) > Number(this.selectedEnd.replace(':', ''))) {
          const start = this.selectedStart;
          const end = this.selectedEnd;

          this.selectedStart = end;
          this.selectedEnd = start;
        }
      }

      this.openTaskTimeInfo(e);
    }
  }

  setTaskDateTime() {
    this.closeDialog(true,
      {
        day: this.dateControl.value,
        time: this.selectedStart,
        endTime: this.selectedEnd ? this.selectedEnd : '',
        schedule: this.scheduler$.getValue()
      });
  }

  openTaskTimeInfo(e: any) {
    this.closeTaskTimeInfo();
    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo({ x: e.clientX, y: e.clientY })
      .withPositions([
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'end',
          overlayY: 'top',
        }
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close()
    });

    this.overlayRef.attach(new TemplatePortal(this.taskTimeInfo, this.viewContainerRef));

    this.sub = fromEvent<MouseEvent>(document, 'click')
      .pipe(
        filter(event => {
          const clickTarget = event.target as HTMLElement;
          return !!this.overlayRef && !this.overlayRef.overlayElement.contains(clickTarget);
        }),
        take(1)
      ).subscribe(() => this.closeTaskTimeInfo());
  }

  clearTimes() {
    this.selectedStart = undefined;
    this.selectedEnd = undefined;
    this.closeTaskTimeInfo();
  }

  closeTaskTimeInfo() {
    if (this.sub) {
      this.sub.unsubscribe();
      if (this.overlayRef) {
        this.overlayRef.dispose();
        this.overlayRef = null;
      }
    }
  }

  setColours(response: TaskGridResponse[]) {
    let tasks: TaskScheduleResponse[] = [];
    if (response) {
      response.map((item: TaskGridResponse) => {
        // @ts-ignore
        tasks = [...tasks, ...item.tasks];
      });
    }

    tasks.filter(item => item.busy).map((item: TaskScheduleResponse) => {
      this.colors.push({
        id: item.taskId!,
        color: this.config.randomBackgroundColors[Math.floor(Math.random() * this.config.randomBackgroundColors.length)]
      });
    });
  }

  getColor(id: string) {
    if (id) {
      const color = this.colors.find(item => item.id === id)?.color
        ? this.colors.find(item => item.id === id)?.color
        : this.config.randomBackgroundColors[0];
      return {backgroundColor: color};
    }

    return {backgroundColor: '#eefbf0'};
  }

  /** Close dialog */
  closeDialog(result: boolean, data?: any) {
    this.dialogRef.close({result, data});
  }
}
